import React from "react";
import PropTypes from "prop-types";
import FederationUserDataComponent from "./FederationUserDataComponent";
import {setFormState} from "../../util/FormUtil";

import {
    PROP_NAME_PHONE_2FA_CHANNEL,
    PROP_NAME_PHONE_COUNTRY_CODE,
    PROP_NAME_PHONE_EXTENSION,
    PROP_NAME_PHONE_NUMBER
} from "./input/PhoneInputComponent";
import {isSame} from "../../util/StringUtil";
import PhoneVerificationComponent from "./PhoneVerificationComponent";
import UserDataService from "../common/UserDataService";
import {ERROR_CODE_BAD_INPUT, extractMessage, fetchResponseHandler} from "../../util/FetchUtil";
import ProcessingComponent from "../../util/ProcessingComponent";
import AlertComponent from "../../util/AlertComponent";

const PAGE_FORM = "formPage";
const PAGE_VERIFY_PHONE = "verifyPhonePage";

class ConvertExternalToCglComponent extends React.Component {

    constructor(props){
        super(props);
        const {seedData} = props;
        this.state = {
            data: seedData ? seedData : {},
            metadata: {},
            page: PAGE_FORM
        };
        this.setFormState = this.setFormState.bind(this);
        this.verifyPhoneNumber = this.verifyPhoneNumber.bind(this);
        this.setPage = this.setPage.bind(this);
        this.setFailToRetrievePhoneFromDuo = this.setFailToRetrievePhoneFromDuo.bind(this);
        this.convertExternalToCgl = this.convertExternalToCgl.bind(this);
        this.setShowProcessing = this.setShowProcessing.bind(this);
        this.showProcessing = this.showProcessing.bind(this);
        this.hideProcessing = this.hideProcessing.bind(this);
        this.setErrorMessage = this.setErrorMessage.bind(this);
        this.removeMessage = this.removeMessage.bind(this);
        this.isPhoneDataAlreadyVerified = this.isPhoneDataAlreadyVerified.bind(this);
        this.isPhoneDataSameAsSeed = this.isPhoneDataSameAsSeed.bind(this);
        this.setExtractData = this.setExtractData.bind(this);
        this.cancel = this.cancel.bind(this);
        this.backToForm = this.backToForm.bind(this);
    }

    setFormState(...args){
        this.setState(state => setFormState(state, args));
    }

    verifyPhoneNumber(){
        const {phoneVerificationReceipt} = this.state;
        if ( this.isPhoneDataSameAsSeed() || this.isPhoneDataAlreadyVerified()){
            // everything is the same, no need to verify
            this.convertExternalToCgl(phoneVerificationReceipt);
        } else {
            this.setPage(PAGE_VERIFY_PHONE);
        }
    }

    setPage(page){
        this.setState(state => ({...state, page}));
    }

    setErrorMessage(e){
        const errorMessage = extractMessage(e);
        this.setState(state => ({...state, errorMessage}));
    }

    setFailToRetrievePhoneFromDuo (failToRetrievePhoneFromDuo) {
        this.setState(state => ({...state, failToRetrievePhoneFromDuo}));
    }

    isPhoneDataSameAsSeed(){
        const {seedData} = this.props;
        const {data} = this.state;

        const result =
            isSame(seedData[PROP_NAME_PHONE_COUNTRY_CODE], data[PROP_NAME_PHONE_COUNTRY_CODE]) &&
            isSame(seedData[PROP_NAME_PHONE_NUMBER], data[PROP_NAME_PHONE_NUMBER]) &&
            isSame(seedData[PROP_NAME_PHONE_EXTENSION], data[PROP_NAME_PHONE_EXTENSION]) &&
            isSame(seedData[PROP_NAME_PHONE_2FA_CHANNEL], data[PROP_NAME_PHONE_2FA_CHANNEL]);

        return result;
    }

    isPhoneDataAlreadyVerified(){
        const {data} = this.state;
        const {verifiedPhone} = this.state;
        if (! verifiedPhone){
            return false;
        }
        const result =
            isSame(verifiedPhone.phoneCountryCode, data[PROP_NAME_PHONE_COUNTRY_CODE]) &&
            isSame(verifiedPhone.phoneNumber, data[PROP_NAME_PHONE_NUMBER]) &&
            isSame(verifiedPhone.phoneExtension, data[PROP_NAME_PHONE_EXTENSION]) &&
            isSame(verifiedPhone.twoFactorChannel, data[PROP_NAME_PHONE_2FA_CHANNEL]);

        return result;
    }

    convertExternalToCgl(phoneVerificationReceipt){
        // since we are saving password, needs to check for password error
        const {userDataService, onSuccess, contextId} = this.props;
        const {data} = this.state;
        const verifiedPhone = {
            phoneCountryCode: data[PROP_NAME_PHONE_COUNTRY_CODE],
            phoneNumber: data[PROP_NAME_PHONE_NUMBER],
            phoneExtension: data[PROP_NAME_PHONE_EXTENSION],
            twoFactorChannel: data[PROP_NAME_PHONE_2FA_CHANNEL]
        };
        this.setState(state => ({...state, verifiedPhone}));
        this.showProcessing();
        const userInput = this.extractData();
        userDataService.convertExternalToCgl({
            phoneVerificationReceipt,
            requestId: contextId,
            ...userInput
        })
            .then(fetchResponseHandler)
            .then(data => {
                if (data.updateReceipt === 'DUO_PHONE_IMPORT_ERROR') {
                    // AREAAA-793: Backend can't retrieve the user's phone number from DUO. Ask user to enter it manually
                    this.setErrorMessage("Error occurred when finding the phone number in your account. Please enter phone number below.")
                    this.setFailToRetrievePhoneFromDuo(true);
                } else {
                    onSuccess(data);
                }
            })
            .catch(e => {
                if (e.errorCode === ERROR_CODE_BAD_INPUT && e.data && e.data.password){
                    const {password={}} = this.state.metadata;
                    this.setFormState({
                        property: "password",
                        metadata: {...password, valid: false, message: e.data.password}
                    });
                    this.setPage(PAGE_FORM);
                    this.setState(state => ({...state, phoneVerificationReceipt}));
                } else {
                    this.setErrorMessage(e);
                }
            })
            .finally(this.hideProcessing)
        ;
    }

    setShowProcessing(showProcessing){
        this.setState(state => ({...state, showProcessing}));
    }

    showProcessing(){
        this.setShowProcessing(true);
    }

    hideProcessing(){
        this.setShowProcessing(false);
    }

    removeMessage(){
        this.setErrorMessage(null);
    }

    setExtractData(func){
        this.extractData = func;
    }

    cancel(){
        const {onCancel} = this.props;
        const {data} = this.state;
        onCancel(data);
    }

    backToForm(){
        this.setPage(PAGE_FORM);
    }

    render(){
        const {userDataInit, userDataService, onCancel, seedData, email, conditionalFed, allowSkipPhoneInput,skipIpStackServiceCall} = this.props;
        const {page, data, metadata, showProcessing, failToRetrievePhoneFromDuo=false, errorMessage} = this.state;
        const hidePhoneInput = allowSkipPhoneInput && !failToRetrievePhoneFromDuo;
        return (
            <div>
                <AlertComponent message={errorMessage} type="error" onClose={this.removeMessage} />
                {
                    page === PAGE_FORM &&
                    <FederationUserDataComponent
                        data={data}
                        metadata={metadata}
                        userDataInit={userDataInit}
                        userDataService={userDataService}
                        requireSecurityQnA={true}
                        includePassword={true}
                        setFormState={this.setFormState}
                        onCancel={this.cancel}
                        onSubmit={hidePhoneInput ? () => this.convertExternalToCgl("PVR-TBD") : this.verifyPhoneNumber}
                        seedData={seedData}
                        email={email}
                        onExtractData={this.setExtractData}
                        phoneRequired={true}
                        conditionalFed={conditionalFed}
                        allowSkipPhoneInput={hidePhoneInput}
                        skipIpStackServiceCall={skipIpStackServiceCall}
                    />
                }
                {
                    page === PAGE_VERIFY_PHONE &&
                    <PhoneVerificationComponent
                        countryCode={data[PROP_NAME_PHONE_COUNTRY_CODE]}
                        phoneNumber={data[PROP_NAME_PHONE_NUMBER]}
                        extension={data[PROP_NAME_PHONE_EXTENSION]}
                        channel={data[PROP_NAME_PHONE_2FA_CHANNEL]}
                        userDataService={userDataService}
                        onError={this.setErrorMessage}
                        onCancel={this.backToForm}
                        onSuccess={this.convertExternalToCgl}
                        securityQnAData={this.extractData().securityQuestionsInfo}
                    />
                }
                <ProcessingComponent show={showProcessing} />
            </div>
        );
    }
}

ConvertExternalToCglComponent.propTypes = {
    contextId: PropTypes.string.isRequired,
    userDataInit: PropTypes.object.isRequired,
    userDataService: PropTypes.instanceOf(UserDataService).isRequired,
    seedData: PropTypes.object,
    onCancel: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    email: PropTypes.string,
    conditionalFed: PropTypes.bool,
};

export default ConvertExternalToCglComponent;
